import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '../hooks/useInView';

const ShowroomTeaser = () => {
  const [ref, isInView] = useInView({ threshold: 0.15 });
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const sectionRef = useRef(null);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Subtle parallax drift effect
  useEffect(() => {
    if (prefersReducedMotion) return;

    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate how far through the viewport the section is
      const progress = (windowHeight - rect.top) / (windowHeight + rect.height);
      // Clamp between 0 and 1
      const clampedProgress = Math.max(0, Math.min(1, progress));
      // Convert to a subtle offset (-15px to +15px)
      setParallaxOffset((clampedProgress - 0.5) * 30);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prefersReducedMotion]);

  return (
    <section
      id="showroom"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{
        minHeight: '520px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* Background Layer - Blurred Showroom Photo */}
      <div
        className="absolute inset-0 z-0"
        style={{
          opacity: isInView ? 1 : 0,
          transition: 'opacity 1.1s ease',
        }}
      >
        {/* Showroom image - blurred and faded */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/showroom/BCD_3723.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(60px)',
            opacity: 0.15,
            transform: prefersReducedMotion ? 'none' : `translateY(${parallaxOffset}px) scale(1.15)`,
            transition: prefersReducedMotion ? 'none' : 'transform 0.1s ease-out',
          }}
        />

        {/* Dark overlay for text readability */}
        <div
          className="absolute inset-0"
          style={{
            background: 'rgba(10, 10, 10, 0.75)',
          }}
        />

        {/* Warm tint overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(180, 140, 80, 0.08) 0%, rgba(120, 90, 50, 0.05) 100%)',
          }}
        />

        {/* Vignette effect */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0, 0, 0, 0.4) 100%)',
          }}
        />

        {/* Subtle noise texture */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            opacity: 0.03,
            mixBlendMode: 'overlay',
          }}
        />

        {/* Top fade to blend with previous section */}
        <div
          className="absolute top-0 left-0 right-0 h-32"
          style={{
            background: 'linear-gradient(to bottom, #0a0a0a 0%, transparent 100%)',
          }}
        />

        {/* Bottom fade to blend with next section */}
        <div
          className="absolute bottom-0 left-0 right-0 h-32"
          style={{
            background: 'linear-gradient(to top, #0a0a0a 0%, transparent 100%)',
          }}
        />
      </div>

      {/* Content */}
      <div
        ref={ref}
        className="relative z-10 w-full max-w-[1320px] mx-auto px-6 py-24"
      >
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          style={{
            opacity: isInView ? 1 : 0,
            transform: isInView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s',
          }}
        >
          {/* Left: Headline + Description */}
          <div>
            <p
              className="text-[12px] mb-4 uppercase tracking-[0.2em]"
              style={{
                color: 'rgba(180, 160, 120, 0.7)',
              }}
            >
              Our Space
            </p>
            <h2 className="text-[40px] md:text-[52px] font-extralight text-white mb-6 tracking-[-0.02em] leading-[1.1]">
              Our Showroom
            </h2>
            <p className="text-[#9a9a9a] text-[16px] leading-[1.75] max-w-[480px]">
              Where brands become assortments. Our New Jersey showroom is built for hands-on review and line building with retail partners.
            </p>
          </div>

          {/* Right: CTA */}
          <div className="lg:text-right">
            <div className="inline-flex flex-col sm:flex-row gap-4">
              <Link
                to="/showroom"
                className="group inline-flex items-center justify-center px-8 py-4 bg-white text-[#0a0a0a] text-[14px] font-semibold transition-all duration-300"
                style={{
                  borderRadius: '6px',
                  boxShadow: '0 4px 20px rgba(255, 255, 255, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(255, 255, 255, 0.1)';
                }}
              >
                Schedule a Visit
              </Link>
              <Link
                to="/showroom"
                className="inline-flex items-center justify-center px-8 py-4 border text-white text-[14px] font-medium transition-all duration-300"
                style={{
                  borderRadius: '6px',
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                  background: 'rgba(255, 255, 255, 0.02)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                }}
              >
                View Showroom
              </Link>
            </div>

            {/* Location hint */}
            <p className="mt-6 text-[#5a5a5a] text-[13px]">
              Keasbey, New Jersey • By appointment only
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShowroomTeaser;
